<template>
    <div>
        <div class="filters-container p-3">
            <div class="col-lg-9 col-md-10 mx-auto d-lg-flex d-block justify-content-around align-items-center">
                <div ref="searchWasteRef" class="h6 my-lg-0 my-3">
                    <label for="searchWaste">{{ model.langSearchWaste }}</label>
                </div>
                <div class="search-input col-lg-4 px-0">
                    <autocomplete id="searchWaste"
                                  ref="autocomplete"
                                  :model="query"
                                  :search="searchSuggestions"
                                  :placeholder="model.langSearchPlaceholder"
                                  :aria-label="model.langSearchPlaceholder"
                                  :default-value="queryInput"
                                  :get-result-value="getSuggestionText"
                                  @submit="handleQueryUpdate"
                                  @click="clearField">
                        <template #result="{ result, props }">
                            <li class="suggestions-text">{{ model.suggestionsLabel }}:</li>
                            <li v-bind="props"
                                class="autocomplete-result">
                                {{ result.text }}
                            </li>
                        </template>
                    </autocomplete>
                    <button class="button" :model="query" type="submit" @click="submit">{{ model.searchButtonLabel }}</button>
                </div>
                <div class="pipe d-lg-block d-none"></div>
                <div class="h6 my-lg-0 my-3">
                    <span id="limitSearch">{{ model.langLimitSearch }}</span>
                </div>
                <div role="status" class="sr-only">{{ categoryAriaStatus }}</div>
                <vue-select 
                  id="category" 
                  v-model="selectedCategory" 
                  class="col-lg-3 px-0" 
                  :options="categories" 
                  :value="selectedCategory" 
                  :placeholder="model.langCategoryPlaceholder" 
                  label="value"
                  aria-labelledby="limitSearch"
                  @input="handleCategoriesSelect"
                >
                    <template slot="option" slot-scope="option">
                        <span :aria-label="option.value">{{ option.value }} ({{ option.matchedCount }}/{{ option.totalCount }})</span>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                        <span :aria-label="option.value">{{ option.value }} ({{ getSelectedCategory(option.value).matchedCount }}/{{ getSelectedCategory(option.value).totalCount }})</span>
                    </template>
                </vue-select>
            </div>
        </div>
        <div class="container-fluid">
            <div v-if="loading" class="text-center my-3">
                <img src="/img/ajax-loader.gif" />
            </div>
            <h1 v-if="!loading && hits.length > 0" class="number-of-results h3 text-center pt-4 pb-3" v-html="totalText"></h1>
            <div v-if="!loading && hits.length == 0 && !!model.noResultsText" class="text-center pt-5" v-html="model.noResultsText"></div>
            <div v-for="hit in hits" :key="hit.id" class="col-12">
                <div :ref="'hit' + hit.id" :aria-labelledby="'hit-' + hit.id" class="d-flex waste-card box pb-2">
                    <div :id="'hit-' + hit.id" class="col-11 px-0">
                        <div class="d-flex align-items-center">
                            <a :href="hit.url" class="title-link h6 mb-3 mt-0 d-flex align-items-center" @click.prevent="handleHitClick(hit)">
                                <span class="arrow-icon mr-2" aria-hidden="true">
                                    <svg class="icon icon-hsy-icon-arrow-right-bold"><use xlink:href="#icon-hsy-icon-arrow-right-bold"></use></svg>
                                </span>
                                {{ hit.title }}
                            </a>
                        </div>
                        
                        <span v-if="hit.synonyms.synonyms.length" class="sr-only">{{ hit.synonyms.heading }}</span>
                        <p v-if="hit.synonyms.synonyms.length" v-html="hit.synonyms.synonyms.join(', ')"></p>
                        <span v-if="hit.recyclingTags.tags.length" class="sr-only">{{ hit.recyclingTags.heading }}</span>
                        <div v-for="(category, index) in hit.recyclingTags.tags" :key="index" class="recycling-tag">
                            <a v-if="category.url != null" :href="category.url" :class="{ 'black-text': category.darkText }"
                               class="d-inline-flex mb-3 mr-4 recycling-method tag align-items-center" :style="{backgroundColor: category.color}">
                                {{ category.title }}
                            <svg class="icon icon-hsy-icon-arrow-right-bold ml-2" aria-hidden="true"><use xlink:href="#icon-hsy-icon-arrow-right-bold"></use></svg>
                            </a>
                            <span v-else :class="{ 'black-text': category.darkText }"
                                  class="d-inline-flex mb-3 mr-4 recycling-method tag align-items-center" :style="{backgroundColor: category.color}">
                                {{ category.title }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <button v-if="showLoadMore" class="button show-more d-flex my-4 align-items-center" @click="more">
            {{ model.langLoadMore }}
            <span class="arrow-icon d-flex justify-content-center align-items-center ml-2" aria-hidden="true">
                <svg class="icon icon-hsy-icon-plus-bold"><use xlink:href="#icon-hsy-icon-plus-bold"></use></svg>
            </span>
        </button>
    </div>
</template>
<script>
    import axios from 'axios';
    import vueSelect from 'vue-select';
    import DOMPurify from 'dompurify';

    export default {
        components: {
            vueSelect
        },
        props: {
            model: {
                type: Object,
                required: true
            }
        },
        data: () => {
            return {
                categories: [],
                selectedCategory: null,
                categoryAriaStatus: '',
                hits: [],
                query: '',
                loading: false,
                page: 0,
                showLoadMore: false,
                total: 0,
                totalText: null,
                queryInput: ''
            }
        },
        watch: {
            queryInput: function () {
                if (this.queryInput == '') {
                    this.handleQueryUpdate();
                }
            }
        },

        beforeMount() {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has("query")) {
                this.queryInput = decodeURIComponent(urlParams.get("query"));
            }
            if (urlParams.has("categories")) {
                this.selectedCategory = decodeURIComponent(urlParams.get("categories"));
            }
        },
        mounted() {
            this.query = this.queryInput;
            this.search();
            this.$refs.searchWasteRef.focus();
        },
        methods: {
            clearField() {
                this.$refs.autocomplete.setValue('');
            },
            searchSuggestions(input) {
                this.queryInput = input;
                const url = `${this.model.autoSuggestionsUrl}?query=${encodeURI(input)}`
                return new Promise(resolve => {
                    if (input.length < 1) {
                        return resolve([])
                    }

                    fetch(url)
                        .then(response => response.json())
                        .then(data => {
                            resolve(data.hits)
                        })
                })
            },
            getSuggestionText(suggestion) {
                return (suggestion || {}).text;
            },
            search: function (clear) {
                if (clear) {
                    this.hits = [];
                    this.page = 0;
                }
                this.loading = true;
                axios.get(`${this.model.searchUrl}`, {
                    params: {
                        page: this.page + 1,
                        pageSize: this.model.pageSize,
                        query: this.query,
                        category: this.selectedCategory
                    }
                })
                    .then(response => {
                        this.categories = response.data.categories;
                        this.hits = this.hits.concat(response.data.hits);
                        this.loading = false;
                        this.total = response.data.totalCount;
                        this.showLoadMore = this.hits.length < this.total;
                        this.totalText = this.model.langFoundNResultsFor.replace("{0}", DOMPurify.sanitize(`<b>${this.query}</b>`, { ALLOWED_TAGS: ['b'] })).replace("{1}", this.total);
                    }).catch(error => {
                        this.loading = false;
                        this.showLoadMore = false;
                        console.log(error);
                    });
            },
            more: function () {
                if (!this.loading) {
                    this.setFocus();
                    this.page = this.page + 1;
                    this.search();
                }
            },
            setFocus: function () {
                if (this.hits.length > 0) {
                    let last = this.hits[this.hits.length - 1];
                    let element = this.$refs["hit" + last.id][0]
                    if (element) {
                        element.focus();
                    }
                }
            },
            handleQueryUpdate(selectedSuggestion) {
                if (selectedSuggestion) {
                    this.goToPage(selectedSuggestion.url)
                }
                else {
                    this.query = this.queryInput;
                    const urlParams = new URLSearchParams(window.location.search);
                    urlParams.set("query", this.query);
                    window.history.replaceState({}, document.title, "?" + urlParams.toString());
                    this.search(true);
                }
            },
            handleCategoriesSelect(value) {
                const urlParams = new URLSearchParams(window.location.search);
                if (value) {
                    this.selectedCategory = value.value;
                    urlParams.set("categories", this.selectedCategory);
                    this.categoryAriaStatus = "";
                }
                else {
                    this.selectedCategory = null;
                    urlParams.delete("categories");
                    this.categoryAriaStatus = this.model.langCategorySelectionEmptied;
                }
                window.history.replaceState({}, document.title, "?" + urlParams.toString());
                this.search(true);
            },
            getSelectedCategory(value) {
                var category = this.categories.find((category) => category.value == value);
                if (!category) {
                    category = {};
                }
                return category;
            },
            getQueryFromUrl() {
                const urlParams = new URLSearchParams(window.location.search);
                if (urlParams.has("query")) {
                    return urlParams.get("query");
                }
            },
            goToPage(pageUrl) {
                window.location.href = pageUrl
            },
            submit() {
                this.query = this.queryInput;
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set("query", this.query);
                window.history.replaceState({}, document.title, "?" + urlParams.toString());
                this.search(true);
            },
            handleHitClick(hit) {
                if (hit.trackUrl) {
                    axios.get(hit.trackUrl).then(() => {
                        hit.trackUrl = null;
                        if (hit.url) window.location.href = hit.url;
                    })
                }
                else {
                    if (hit.url) window.location.href = hit.url;
                }
            }
        }
    }
</script>